import React, { Fragment, useEffect, useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { TabGroup, TabList, TabPanels, Tab, TabPanel } from "@headlessui/react";
import Prism from "prismjs";

export const ProjectSection = ({ pjName, pjdescrip, techShare }) => {
  const codeContainerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: codeContainerRef,
    layoutEffect: false,
  });

  useEffect(() => {
    setTimeout(() => {
      Prism.highlightAll();
    }, 0);
  }, []);

  return (
    <section
      aria-labelledby="features-heading"
      className="mx-auto max-w-7xl py-32 sm:px-2 lg:px-8"
    >
      <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
        <div className="max-w-3xl">
          <h2
            id="features-heading"
            className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
          >
            {pjName}
          </h2>
          <p className="mt-4 text-gray-500">{pjdescrip}</p>
        </div>

        <TabGroup className="mt-4">
          <div className="-mx-4 flex overflow-x-auto sm:mx-0">
            <div className="flex-auto border-b border-gray-200 px-4 sm:px-0">
              <TabList className="-mb-px flex space-x-10">
                {techShare.map((tech, index) => (
                  <Tab
                    key={tech.id || `${tech.childName}-${index}`} // 使用唯一的键
                    className="whitespace-nowrap border-b-2 border-transparent py-6 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 data-[selected]:border-indigo-500 data-[selected]:text-indigo-600"
                  >
                    {tech.childName}
                  </Tab>
                ))}
              </TabList>
            </div>
          </div>

          <TabPanels as={Fragment}>
            {techShare.map((tech, index) => (
              <TabPanel
                key={tech.id || `${tech.childName}-${index}`} // 使用唯一的键
                className="space-y-16 pt-10 lg:pt-16"
              >
                {tech.features.map((feature, featureIndex) => (
                  <div
                    key={feature.id || `${feature.name}-${featureIndex}`} // 使用唯一的键
                    className="flex flex-col-reverse lg:grid lg:grid-cols-12 lg:gap-x-8"
                  >
                    <div className="mt-6 lg:col-span-5 lg:mt-0">
                      <h3 className="text-lg font-medium text-gray-900">
                        <a
                          href={feature.name}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {feature.name}
                        </a>
                      </h3>

                      <div className="mt-6 lg:col-span-5 lg:mt-0">
                        <h3 className="text-lg font-medium text-gray-900">
                          <a
                            href={feature.web}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {feature.web}
                          </a>
                        </h3>
                        <p className="mt-2 text-sm pt-10 text-gray-500">
                          {feature.description}
                        </p>
                      </div>

                      <motion.div
                        className="absolute top-0 left-0 h-1 bg-indigo-600"
                        style={{ scaleX: scrollYProgress }}
                        initial={{ scaleX: 0 }}
                        transition={{ ease: "easeOut", duration: 0.2 }}
                      />

                      {feature.code && (
                        <div ref={codeContainerRef} className="relative mt-4">
                          <div className="mx-auto max-w-2xl sm:mx-0 sm:max-w-none scale-90">
                            <div className="overflow-hidden rounded-tl-xl bg-gray-900 ring-1 ring-white/10">
                              <div className="flex bg-gray-800/40 ring-1 ring-white/5">
                                <div className="border-b border-r border-b-white/20 border-r-white/10 bg-white/5 px-4 py-2 text-white">
                                  {feature.codeHeading}
                                </div>
                                <div className="border-r border-gray-600/10 text-slate-400 px-4 py-2">
                                  Code
                                </div>
                              </div>
                              <div className="px-4 pb-14 pt-6 text-sm h-64 overflow-y-auto">
                                <pre className="language-javascript">
                                  <code className="language-javascript">
                                    {feature.code}
                                  </code>
                                </pre>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="lg:col-span-7 my-auto mx-auto min-h-64">
                      <div className="aspect-h-1 aspect-w-2 overflow-hidden rounded-lg sm:aspect-h-2 sm:aspect-w-5">
                        <img
                          alt={feature.imageAlt}
                          src={feature.imageSrc}
                          className="object-cover object-center max-h-96"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
      </div>
    </section>
  );
};
